import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Phone, Menu, X, ChevronDown, Facebook, Instagram, Youtube, Globe, Sun, Moon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/hooks/use-theme";
import Picture from "@/components/ui/picture";

const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  const navItems = [
    {
      label: t.nav.chiSiamo,
      path: "/chi-siamo",
      children: [
        { label: t.nav.storia, path: "/chi-siamo/storia" },
        { label: t.nav.organizzazione, path: "/chi-siamo/organizzazione" },
        { label: t.nav.noiEAnpas, path: "/chi-siamo/noi-e-anpas" },
        { label: t.nav.trasparenza, path: "/chi-siamo/trasparenza" },
        { label: t.nav.nostriMezzi, path: "/chi-siamo/i-nostri-mezzi" },
      ],
    },
    {
      label: t.nav.servizi,
      path: "/servizi",
      children: [
        { label: t.nav.trasportoSanitario, path: "/servizi/trasporto-sanitario-programmato" },
        { label: t.nav.teleassistenza, path: "/servizi/teleassistenza" },
        { label: t.nav.assistenzaEventi, path: "/servizi/assistenza-manifestazioni-eventi" },
        { label: t.nav.corsiFormazione, path: "/servizi/corsi-formazione-sanitaria" },
        { label: t.nav.continuitaAssistenziale, path: "/servizi/assistenza-guardia-medica" },
        { label: t.nav.servizioCivile, path: "/servizi/servizio-civile" },
        { label: t.nav.emergenza112, path: "/servizi/emergenza-sanitaria-112" },
      ],
    },
    {
      label: t.nav.iniziative,
      path: "/iniziative",
      children: [
        { label: t.nav.stellaAmbulanza, path: "/iniziative/una-stella-in-ambulanza" },
        { label: t.nav.progettiAssociazioni, path: "/iniziative/progetti-con-altre-associazioni" },
      ],
    },
    {
      label: t.nav.newsEventi,
      path: "/news",
      children: [
        { label: t.nav.news, path: "/news" },
        { label: t.nav.eventi, path: "/eventi" },
      ],
    },
    {
      label: t.nav.contatti,
      path: "/contatti",
      children: [
        { label: t.nav.leSedi, path: "/contatti/le-sedi" },
        { label: t.nav.lavoraConNoi, path: "/contatti/lavora-con-noi" },
      ],
    },
    {
      label: t.nav.sostienici,
      path: "/sostienici",
      children: [
        { label: t.nav.donazioni5x, path: "/sostienici/5x1000-donazioni" },
        { label: t.nav.diventaAmico, path: "/sostienici/diventa-amico-avps" },
      ],
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleLang = () => setLang(lang === "it" ? "en" : "it");

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-background backdrop-blur-md border-b border-border"
          : "bg-background/95 backdrop-blur-sm"
      )}
      style={scrolled ? { boxShadow: 'var(--shadow-header)' } : undefined}
    >
      {/* Top bar */}
      <div className="hidden sm:block bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-1.5 text-xs">
          <div className="flex items-center gap-4">
            <a href="tel:0396612112" className="flex items-center gap-1.5 hover:text-primary-foreground/80 transition-colors font-medium">
              <Phone className="w-3.5 h-3.5" /> 039 66 12 112
            </a>
            <span className="hidden md:inline text-primary-foreground/85">{t.emergency24}</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:segreteria@avps.it" className="hover:text-primary-foreground/80 transition-colors hidden md:inline">segreteria@avps.it</a>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-1 hover:text-primary-foreground/80 transition-colors"
              aria-label={theme === "light" ? "Attiva tema scuro" : "Attiva tema chiaro"}
            >
              {theme === "light" ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 hover:text-primary-foreground/80 transition-colors font-medium uppercase"
              aria-label="Switch language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "it" ? "EN" : "IT"}
            </button>
            <div className="flex items-center gap-3 text-primary-foreground/80">
              <a href="https://login.microsoftonline.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-primary-foreground/10 transition-colors text-xs font-medium">
                <Lock className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t.nav.areaRiservata}</span>
              </a>
              <a href="https://www.facebook.com/avpsvimercate/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-primary-foreground transition-colors"><Facebook className="w-3.5 h-3.5" /></a>
              <a href="https://www.instagram.com/avps_vimercate/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-primary-foreground transition-colors"><Instagram className="w-3.5 h-3.5" /></a>
              <a href="https://www.youtube.com/user/AVPSVimercate" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-primary-foreground transition-colors"><Youtube className="w-3.5 h-3.5" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24 md:h-28">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <Picture src="/images/logo_avps_official_clean.png" alt="AVPS Vimercate" width={520} height={354} className="h-20 md:h-28 w-auto block dark:hidden" />
          <Picture src="/images/logo_avps_official_dark.png" alt="AVPS Vimercate" width={520} height={354} className="h-20 md:h-28 w-auto hidden dark:block" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5" role="navigation" aria-label={lang === "it" ? "Navigazione principale" : "Main navigation"}>
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative group"
              onMouseEnter={() => setOpenDropdown(item.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to={item.path}
                className={cn(
                  "flex items-center gap-1 px-3 py-2 text-[13px] font-semibold font-heading rounded-lg transition-colors",
                  "text-foreground/75 hover:text-primary hover:bg-secondary"
                )}
              >
                {item.label}
                {item.children && <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />}
              </Link>
              {item.children && openDropdown === item.label && (
                <div className="absolute top-full left-0 pt-1.5 min-w-[230px] z-50">
                  <div className="bg-background border border-border rounded-xl p-2 shadow-lg animate-fade-in">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-4 py-2.5 text-sm rounded-lg text-foreground/75 hover:text-primary hover:bg-secondary transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-2">
          <Button variant="outline" size="sm" asChild className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/diventa-volontario">{t.nav.diventaVolontario}</Link>
          </Button>
          <Button variant="cta" size="sm" asChild>
            <Link to="/sostienici">{t.nav.sostienici}</Link>
          </Button>
        </div>

        {/* Mobile burger */}
        <div className="lg:hidden flex items-center gap-1">
          <a
            href="https://login.microsoftonline.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-foreground"
            aria-label={t.nav.areaRiservata}
          >
            <Lock className="w-5 h-5" />
          </a>
          <button
            onClick={toggleTheme}
            className="p-2 text-foreground"
            aria-label={theme === "light" ? "Dark mode" : "Light mode"}
          >
            {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
          <button
            onClick={toggleLang}
            className="p-2 text-foreground"
            aria-label="Switch language"
          >
            <Globe className="w-5 h-5" />
          </button>
          <button
            className="p-2 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background border-t border-border max-h-[80vh] overflow-y-auto">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-1">
            {navItems.map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between">
                  <Link to={item.path} className="block py-2.5 text-sm font-semibold text-foreground/80 hover:text-primary">
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      className="p-2"
                      aria-label={`Expand ${item.label}`}
                    >
                      <ChevronDown className={cn("w-4 h-4 transition-transform", openDropdown === item.label && "rotate-180")} />
                    </button>
                  )}
                </div>
                {item.children && openDropdown === item.label && (
                  <div className="pl-4 space-y-0.5 pb-2">
                    {item.children.map((child) => (
                      <Link key={child.path} to={child.path} className="block py-2 text-sm text-muted-foreground hover:text-primary">
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 flex flex-col gap-2 border-t border-border mt-2">
              <Button variant="outline" asChild className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/diventa-volontario">{t.nav.diventaVolontario}</Link>
              </Button>
              <Button variant="cta" asChild>
                <Link to="/sostienici">{t.nav.sostienici}</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
